import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationMap = () => {
  const [mapLoaded, setMapLoaded] = useState(false);

  const officeLocation = {
    name: 'Endosurgery Sdn Bhd Headquarters',
    address: 'No. 15, Jalan Teknologi 3/6, Taman Sains Selangor 1, Kota Damansara, 47810 Petaling Jaya, Selangor, Malaysia',
    coordinates: {
      lat: 3.1725,
      lng: 101.5847
    },
    landmarks: [
      'Near Kota Damansara MRT Station',
      'Adjacent to Taman Sains Selangor',
      'Close to The Curve Shopping Mall',
      '15 minutes from Kuala Lumpur city center'
    ]
  };

  const transportOptions = [
    {
      icon: 'Train',
      title: 'MRT/LRT',
      description: 'Kota Damansara MRT Station (5 minutes walk)',
      color: 'text-blue-600'
    },
    {
      icon: 'Car',
      title: 'By Car',
      description: 'Free parking available on-site',
      color: 'text-green-600'
    },
    {
      icon: 'Bus',
      title: 'Bus',
      description: 'Multiple bus routes to Kota Damansara',
      color: 'text-orange-600'
    },
    {
      icon: 'Plane',
      title: 'From Airport',
      description: '45 minutes from KLIA via KLIA Express + MRT',
      color: 'text-purple-600'
    }
  ];

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const openInGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${officeLocation?.coordinates?.lat},${officeLocation?.coordinates?.lng}`;
    window.open(url, '_blank');
  };

  const openDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${officeLocation?.coordinates?.lat},${officeLocation?.coordinates?.lng}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Map Header */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-text-primary mb-3 flex items-center justify-center">
          <Icon name="MapPin" size={24} className="mr-3" />
          Visit Our Office
        </h2>
        <p className="text-text-secondary">
          Located in the heart of Kota Damansara's technology hub, easily accessible by public transport and car.
        </p>
      </div>
      {/* Interactive Map */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-lg overflow-hidden shadow-medical"
      >
        <div className="relative h-80 bg-muted">
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Icon name="MapPin" size={48} className="text-text-secondary mx-auto mb-2" />
                <p className="text-text-secondary">Loading map...</p>
              </div>
            </div>
          )}
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title={officeLocation?.name}
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps?q=${officeLocation?.coordinates?.lat},${officeLocation?.coordinates?.lng}&z=16&output=embed`}
            onLoad={handleMapLoad}
            className="w-full h-full"
          />
        </div>
        
        {/* Map Controls */}
        <div className="p-4 bg-card border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex-1">
              <h3 className="font-semibold text-text-primary mb-1">
                {officeLocation?.name}
              </h3>
              <p className="text-sm text-text-secondary">
                {officeLocation?.address}
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openInGoogleMaps}
                iconName="ExternalLink"
                iconPosition="left"
              >
                View in Maps
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={openDirections}
                iconName="Navigation"
                iconPosition="left"
              >
                Get Directions
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
      {/* Transportation Options */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Route" size={20} className="mr-2" />
          How to Reach Us
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {transportOptions?.map((option, index) => (
            <div
              key={index}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted transition-colors duration-200"
            >
              <div className="flex-shrink-0">
                <Icon name={option?.icon} size={20} className={option?.color} />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-text-primary text-sm mb-1">
                  {option?.title}
                </h4>
                <p className="text-text-secondary text-xs">
                  {option?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Nearby Landmarks */}
      <div className="bg-muted rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Landmark" size={20} className="mr-2" />
          Nearby Landmarks
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {officeLocation?.landmarks?.map((landmark, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <span className="text-sm text-text-secondary">{landmark}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Parking Information */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Car" size={24} color="var(--color-primary)" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-primary mb-2">
              Parking & Accessibility
            </h3>
            <div className="text-text-secondary text-sm space-y-1">
              <p>• Free parking spaces available for visitors</p>
              <p>• Wheelchair accessible entrance and facilities</p>
              <p>• Dedicated parking for medical professionals</p>
              <p>• Security-monitored parking area</p>
              <p>• Electric vehicle charging stations available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationMap;